import { Injectable } from '@angular/core';
import moment from 'moment';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { FileTypeService } from '../file-type.service';
import { NotificationMessage, NotificationObject, UserProfileSettings } from './notification.types';

@Injectable({
  providedIn: 'root',
})
export class CommunicationActivityNotificationHandlerService {
  private static readonly VOTE_STATUS_MAP: Record<string, string> = {
    YES: 'edit_vote_approval',
    NEUTRAL: 'edit_vote_neutralVote',
    NO: 'edit_vote_rejection',
  };

  constructor(
    private readonly translatePipe: TranslatePipe,
    private readonly fileTypeService: FileTypeService
  ) {}

  /**
   * Find and format file name from notification object
   */
  private findFileName(object: NotificationObject): string {
    return object.fileName
      ? object.fileName
          .split(', ')
          .map((f: string) => {
            const filename: string = f.substring(f.lastIndexOf('/') + 1);
            if (filename.lastIndexOf('.') - filename.lastIndexOf('_') === 14) {
              return filename.substring(0, filename.lastIndexOf('_')) + filename.substring(filename.lastIndexOf('.'));
            }
            return filename;
          })
          .join(', ')
      : '';
  }

  /**
   * Determine file type and return translated label
   */
  private findFileType(file: { contentType?: string; name?: string; type: null; publicLink: null }): string {
    if (this.fileTypeService.isVideoFile(file)) return this.translatePipe.transform('fileTypeVideo');
    else if (this.fileTypeService.isImageFile(file)) return this.translatePipe.transform('fileTypePhoto');
    else return this.translatePipe.transform('fileTypeFile');
  }

  /**
   * Get the appropriate notification code for file upload based on context
   */
  private getFileUploadNotificationCode(notificationCode: string, object: NotificationObject): string {
    const baseCode: string = notificationCode.replace('_YOUR', '');
    if (!object.sectionId && !object.conversationId) {
      return `${baseCode}_KEY_FILES`;
    }
    if (object.sectionName) {
      return `${baseCode}_SECTION`;
    }
    if (object.subsectionPartName && object.subsectionPartItemName) {
      return `${baseCode}_ITEM`;
    }
    if (object.conversationTitle) {
      return object.conversationType === 'ONE_TO_ONE'
        ? `${notificationCode}_CONVERSATION_121`
        : `${baseCode}_CONVERSATION`;
    }
    return '';
  }

  /**
   * Find and format subsection name
   */
  public findSubsectionName(subSectionName?: string): string {
    if (!subSectionName) {
      return '';
    }
    const translated: string = this.translatePipe.transform(subSectionName);
    let found: string = translated;
    if (subSectionName === translated) {
      subSectionName = subSectionName.replace('_', ' ');
      found = subSectionName;
    }
    return found.toLowerCase().replace(/(^\w)|(\s\w)/g, (match: string) => match.toUpperCase());
  }

  private buildLocationSuffix(object: NotificationObject): string {
    return object.elementName
      ? ` ${this.translatePipe.transform('notification_location_in', { name: object.elementName })}`
      : '';
  }

  // Comment notification handlers
  public handleCommentNotifications(
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    if (originalCode.startsWith('notificationANNOTATION_')) {
      message.label =
        this.translatePipe.transform(originalCode, {
          notifiedBy: object.notifiedBy ?? '',
        }) + this.buildLocationSuffix(object);
    } else {
      message.label = this.translatePipe.transform(originalCode, {
        notifiedBy: object.notifiedBy ?? '',
        subSectionName: this.findSubsectionName(object.subSectionName),
        itemName: object.itemName ?? '',
        version: object.versionNumber ?? '',
      });
    }
  }

  public handleAnnotationTaggedAndCommentReplies(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    if (notificationCode.startsWith('notificationANNOTATION_')) {
      message.label =
        this.translatePipe.transform(notificationCode, {
          notifiedBy: object.notifiedBy ?? '',
          notifiedTarget: object.notifiedTarget ?? '',
        }) + this.buildLocationSuffix(object);
    } else {
      message.label = this.translatePipe.transform(notificationCode, {
        notifiedBy: object.notifiedBy ?? '',
        notifiedTarget: object.notifiedTarget ?? '',
        subSectionName: this.findSubsectionName(object.subSectionName),
        itemName: object.itemName ?? '',
      });
    }
  }

  // Vote notification handlers
  public handleVoteNotifications(originalCode: string, object: NotificationObject, message: NotificationMessage): void {
    let labelContent: string = '';
    if (object.fileVotes) {
      const fileVotes: Record<string, { name: string }> = JSON.parse(object.fileVotes);
      const fileNames: string = Object.values(fileVotes)
        .map((vote: { name: string }) => vote.name)
        .join(', ');
      labelContent = this.translatePipe.transform('notification_batchVoteDescription', {
        file: fileNames,
        option: object.itemName ?? '',
      });
    } else if (object.itemVotes) {
      const itemVotes: Record<string, { name: string; versionNumber?: string }> = JSON.parse(object.itemVotes);
      labelContent = Object.values(itemVotes)
        .map((vote: { name: string; versionNumber?: string }) =>
          this.translatePipe.transform('notification_normalVoteDescription', {
            version: vote.versionNumber ?? object.versionNumber ?? '',
            option: vote.name,
          })
        )
        .join(', ');
    }

    message.label = this.translatePipe.transform(originalCode, {
      notifiedBy: object.notifiedBy ?? '',
      subSectionName: this.findSubsectionName(object.subSectionName),
      items: labelContent,
    });
  }

  public handleVoteCanceled(originalCode: string, object: NotificationObject, message: NotificationMessage): void {
    const previousStatus: string =
      CommunicationActivityNotificationHandlerService.VOTE_STATUS_MAP[object.previousVote ?? ''] ?? '';
    message.label = this.translatePipe.transform(originalCode, {
      notifiedBy: object.notifiedBy ?? '',
      subSectionName: this.findSubsectionName(object.subSectionName),
      itemName: object.itemName ?? '',
      status: this.translatePipe.transform(previousStatus),
      version: object.versionNumber ?? '',
    });
  }

  // File notification handlers
  public handleFileUploaded(notificationCode: string, object: NotificationObject, message: NotificationMessage): void {
    const file: { contentType?: string; name?: string; type: null; publicLink: null } = {
      contentType: object.fileContentType,
      name: object.fileName,
      type: null,
      publicLink: null,
    };
    const fileUploadNotificationCode: string = this.getFileUploadNotificationCode(notificationCode, object);

    message.label = this.translatePipe.transform(fileUploadNotificationCode, {
      notifiedBy: object.notifiedBy ?? '',
      notifiedTarget: object.notifiedTarget ?? '',
      fileType: this.findFileType(file),
      fileName: this.findFileName(object),
      subSectionPartItemName: object.subsectionPartItemName ?? '',
      subsectionPartName: object.subsectionPartName ?? '',
      sectionName: object.sectionName ?? '',
      subSectionName: this.findSubsectionName(object.subSectionName),
      conversationTitle: object.conversationTitle ?? '',
      itemName: object.itemName ?? object.sectionName ?? '',
    });
  }

  public handleFileBatchUploaded(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    const batchCode: string = notificationCode.replace('_YOUR', '').replace('_SELF', '');
    const totalUploads: number =
      Number.parseInt(object.totalSuccess ?? '0', 10) + Number.parseInt(object.totalFailed ?? '0', 10);
    if (totalUploads === 1) {
      message.label = this.translatePipe.transform('notificationFILE_UPLOADED', {
        notifiedBy: object.notifiedBy ?? '',
        fileName: this.findFileName(object),
      });
    } else {
      message.label = this.translatePipe.transform(batchCode, {
        notifiedBy: object.notifiedBy ?? '',
        totalSuccessful: object.totalSuccess ?? '',
        totalFailures: object.totalFailed ?? '',
      });
    }
  }

  public handleFileDeleted(notificationCode: string, object: NotificationObject, message: NotificationMessage): void {
    let fileDeleteNotificationCode: string = notificationCode.replace('_YOUR', '');
    if (!object.sectionId) {
      fileDeleteNotificationCode = `${fileDeleteNotificationCode}_KEY_FILES`;
    }
    message.label = this.translatePipe.transform(fileDeleteNotificationCode, {
      notifiedBy: object.notifiedBy ?? '',
      sectionName: object.sectionName ?? '',
      fileName: this.findFileName(object),
      itemName: object.itemName ?? object.sectionName ?? '',
    });
  }

  public handleFileDownloaded(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    const fileDownloadedNotificationCode: string = notificationCode.replace('_YOUR', '');
    message.label = this.translatePipe.transform(fileDownloadedNotificationCode, {
      notifiedBy: object.notifiedBy ?? '',
      notifiedTarget: object.notifiedTarget ?? '',
      fileName: this.findFileName(object),
      sectionName: object.sectionName ?? '',
      subSectionName: this.findSubsectionName(object.subSectionName),
      assetname: object.assetname ?? object.sectionName ?? '',
    });
  }

  public handleDriveEvent(notificationCode: string, object: NotificationObject, message: NotificationMessage): void {
    message.label = this.translatePipe.transform(notificationCode.replace('_YOUR', ''), {
      notifiedBy: object.notifiedBy ?? '',
      fileName: object.fileName ?? '',
      directoryName: object.directoryName ?? '',
      previousName: object.previousName ?? '',
    });
  }

  // Share notification handlers
  public handleShareInviteSent(originalCode: string, object: NotificationObject, message: NotificationMessage): void {
    message.label = this.translatePipe.transform(originalCode, {
      notifiedBy: object.notifiedBy ?? '',
      surfaceName: object.surfaceName ?? '',
    });
  }

  // Conversation notification handlers
  public handleConversationCallMissed(
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    message.label = this.translatePipe.transform(originalCode);
    message.label = message.label.replace('%NOTIFIEDBY%', object.notifiedBy ?? '');
  }

  public handleInvitationAccepted(
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    message.label = this.translatePipe.transform(originalCode);
    message.label = message.label.replace('%COMPANY_NAME%', object.companyName ?? '');
  }

  public handleInvitationDeclined(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    message.label = this.translatePipe.transform(notificationCode, {
      COMPANY_NAME: object.companyName ?? '',
    });
  }

  public handleVerificationStatus(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    message.label = this.translatePipe.transform(notificationCode, {
      verification_info: object.verificationInfo ?? '',
      verification_status: object.verificationStatus ?? '',
    });
  }

  public handleConversationNotifications(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    message.label = this.translatePipe.transform(notificationCode, {
      title: object.conversationTitle ?? '',
      notifiedBy: object.notifiedBy ?? '',
    });
  }

  public handleConversationUserNotifications(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    message.label = this.translatePipe.transform(notificationCode, {
      notifiedBy: object.notifiedBy ?? '',
      notifiedTarget: object.notifiedTarget ?? '',
      title: object.conversationTitle ?? '',
    });
  }

  public handleConversationMessageSent(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage,
    authUserName: string | null
  ): void {
    const messageLabel: string = `${notificationCode}_${object.conversationType}`;
    message.label = this.translatePipe.transform(messageLabel);
    message.label = message.label.replace(
      '%NOTIFIEDBY%',
      object.notifiedByUsername === authUserName ? (object.notifiedTarget ?? '') : (object.notifiedBy ?? '')
    );
    message.label = message.label.replace('%GROUP%', object.conversationTitle ?? '');
  }

  // Event notification handlers
  public handleEventNotifications(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage,
    userProfileSettings?: UserProfileSettings
  ): void {
    if (!userProfileSettings || !object.eventStart || !object.eventEnd) {
      return;
    }
    message.label = this.translatePipe.transform(notificationCode, {
      eventTitle: object.eventTitle ?? '',
      eventDate: moment(object.eventStart, 'x').format(userProfileSettings.datePattern),
      eventStart: moment(object.eventStart, 'x').format(userProfileSettings.timePattern),
      eventEnd: moment(object.eventEnd, 'x').format(userProfileSettings.timePattern),
      notifiedBy: object.notifiedBy ?? '',
      notifiedTarget: object.notifiedTarget ?? '',
    });
  }

  public handleEventUserNotifications(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage,
    userProfileSettings?: UserProfileSettings
  ): void {
    if (!userProfileSettings || !object.eventStart || !object.eventEnd) {
      return;
    }
    message.label = this.translatePipe.transform(notificationCode, {
      notifiedBy: object.notifiedBy ?? '',
      notifiedTarget: object.notifiedTarget ?? '',
      eventTitle: object.eventTitle ?? '',
      eventDate: moment(object.eventStart, 'x').format(userProfileSettings.datePattern),
      eventStart: moment(object.eventStart, 'x').format(userProfileSettings.timePattern),
      eventEnd: moment(object.eventEnd, 'x').format(userProfileSettings.timePattern),
    });
  }

  // EXT_TASK_* notification handler (new task notification framework)
  public handleExtTaskNotification(
    notificationCode: string,
    object: NotificationObject,
    message: NotificationMessage,
    userProfileSettings?: UserProfileSettings
  ): void {
    const baseCode: string = notificationCode.replace('_YOUR', '').replace('_SELF', '');
    let translated: string = this.translatePipe.transform(notificationCode);
    if (translated === notificationCode) {
      translated = this.translatePipe.transform(baseCode);
    }
    let label: string = translated;

    const newDueDate: string = this.formatExtTaskDate(object.newDueDate, userProfileSettings);

    label = label.replace('%NOTIFIEDBY%', object.notifiedBy ?? '');
    label = label.replace('%TASKTITLE%', object.taskTitle ?? '');
    label = label.replace('%TARGETNAME%', object.targetName ?? '');
    label = label.replace('%OLDSTATUS%', object.oldStatus ?? '');
    label = label.replace('%NEWSTATUS%', object.newStatus ?? '');
    label = label.replace('%OLDPRIORITY%', object.oldPriority ?? '');
    label = label.replace('%NEWPRIORITY%', object.newPriority ?? '');
    label = label.replace('%NEWDUEDATE%', newDueDate);

    message.label = label;
  }

  private formatExtTaskDate(value: string | undefined, userProfileSettings?: UserProfileSettings): string {
    if (!value) {
      return '';
    }
    const parsed: moment.Moment = moment(value);
    if (!parsed.isValid()) {
      return value;
    }
    const pattern: string = userProfileSettings?.datePattern ?? 'MMM D, YYYY';
    return parsed.format(pattern);
  }

  // AI processing notification handlers
  public handleAiProcessingNotifications(
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    const accountName: string = object.account
      ? `${object.account.firstName ?? ''} ${object.account.lastName ?? ''}`.trim()
      : (object.accountEmail ?? '');
    const status: string = (object.processingStatus ?? '').toLowerCase();
    const notificationCode: string = `${originalCode}_${status.toUpperCase()}`;
    const translated: string = this.translatePipe.transform(notificationCode);

    if (translated !== notificationCode) {
      message.label = translated;
    } else {
      message.label = this.translatePipe.transform(originalCode);
    }
    message.label = message.label.replace('%ACCOUNT_NAME%', accountName);
    message.label = message.label.replace('%MESSAGE_TITLE%', object.messageTitle?.replace(/<[^>]*>/g, '') ?? '');
    message.label = message.label.replace('%WIDGET%', object.customWidgetName ?? object.widget ?? '');
    message.label += this.buildLocationSuffix(object);
  }

  // Subtitle notification handlers
  public handleSubtitleNotifications(
    originalCode: string,
    object: NotificationObject,
    message: NotificationMessage
  ): void {
    message.label = this.translatePipe.transform(originalCode);
    message.label = message.label.replace('%NOTIFIEDBY%', object.notifiedBy ?? '');
    message.label = message.label.replace('%SUBTITLENAME%', object.fileName ?? '');
    message.label = message.label.replace('%LANGUAGE%', object.language ?? '');
  }
}
